<script setup lang="ts">
import { ref } from 'vue';
import InstallClipDialog from "./InstallClipDialog.vue";
import PretripClipDialog from "./PretripClipDialog.vue";
import RefuelClipDialog from "./RefuelClipDialog.vue";

interface Props {
  clipNumber: string;
  removalTime: string;
  listType: string;
  eventId: number;
  index: number;
  fuelLevel?: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['update-clips']);
const installClipDialogVisible = ref(false);
const pretripClipDialogVisible = ref(false);
const refuelClipDialogVisible = ref(false);

const removalTimeString = new Date(props.removalTime).toLocaleDateString('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hourCycle: 'h23'
});

</script>

<template>
  <li class="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
    <div class="flex"><i class="pi pi-bolt"></i>
      <div>
        <span class="text-surface-900 dark:text-surface-0 font-medium mb-1">{{ props.clipNumber }}</span>
        <tag class="mx-2" v-if="props.index === 0 && props.listType === 'ready-to-install'" value="Next Out"/>
        <div class="text-surface-600 dark:text-surface-200">Removed {{ removalTimeString }}</div>
        <div v-if="props.fuelLevel" class="text-surface-600 dark:text-surface-200">
          Fuel Level: <span :class="{'text-red-500': props.fuelLevel === 'E' || props.fuelLevel === '1/8' || props.fuelLevel === '1/4' || props.fuelLevel === '3/8' || props.fuelLevel === '5/16' || props.fuelLevel === '7/16' || props.fuelLevel === '1/2' || props.fuelLevel === '9/16' || props.fuelLevel === '5/8' || props.fuelLevel === '11/16' || props.fuelLevel === '3/4' || props.fuelLevel === '13/16'}">{{ props.fuelLevel }}</span>
        </div>
      </div>
    </div>
    <div class="mt-2 md:mt-0 flex flex-nowrap gap-2">
      <!-- Refuel button - visible if fuel level exists and list-type is not fuel-required -->
      <Button v-if="props.fuelLevel && props.listType !== 'fuel-required'" @click="refuelClipDialogVisible = true" outlined size="small" class="p-button-secondary">
        <i class="pi pi-gas-pump mr-1"></i>
        Refuel
      </Button>

      <!-- Action buttons based on list type -->
      <Button v-if="props.listType==='pretrip-required'" @click="pretripClipDialogVisible = true" outlined size="small">Pretrip</Button>
      <Button v-else-if="props.listType==='ready-to-install'" @click="installClipDialogVisible = true" outlined size="small">Install</Button>
      <Button v-else-if="props.listType==='fuel-required'" outlined size="small" disabled>Needs Fuel</Button>
      <Button v-else @click="pretripClipDialogVisible = true" outlined size="small">Resolve</Button>

      <!-- Dialogs -->
      <InstallClipDialog v-model="installClipDialogVisible" :clip-number="props.clipNumber" :event-id="props.eventId" :fuel-level="props.fuelLevel" @update-clips="$emit('update-clips')"></InstallClipDialog>
      <PretripClipDialog v-model="pretripClipDialogVisible" :clip-number="props.clipNumber" :list-type="props.listType" :event-id="props.eventId" @update-clips="$emit('update-clips')"/>
      <RefuelClipDialog v-model="refuelClipDialogVisible" :clip-number="props.clipNumber" :event-id="props.eventId" :current-fuel-level="props.fuelLevel" @update-clips="$emit('update-clips')"/>
    </div>
  </li>
</template>
